import React, { useState, useEffect } from 'react';
import { API, authFetch } from '../utils/api';
import { rangeOf, stepAnchor, rangeLabel, ymdLocal, shiftYmd } from './CancelAgencyJournal';

// 말소대행(cancel_agency) 운영자 대시보드 — CancelAgency.js 의 tab==='dashboard' 블록을 분리한 화면.
//
// 서버 계약은 그대로다 (GET /cancel-agency/dashboard → utils/cancelDashboard.getDashboard):
//   kpi{newCount,inProgressCount,overdueCount,holdCount,reissueCount,monthReceivable,taxInvoiceUnissued}
//   queues{new,overdue,hold,reissue}[] · receivablesTop[] · receivableTotal · taxInvoice{issued,unissued}
//   dailyCompleted[] · scrap{active,completedThisMonth}
// 이 파일은 **표현만** 바꾼다 — 새 호출도, 빠진 정보도 없다.
//
// 기간 선택 (2026-08-31) — 완료 추이 카드의 [최근][일][주][월].
//   ○ 기간을 따르는 것: 완료 추이 차트 + 그 기간 완료 건수/수수료
//   ✕ 따르지 않는 것: 작업 큐 4종 · 미수(Top5/합계) · 세금계산서 — 전부 "지금" 시점 지표다.
//     기간을 씌우면 숫자가 거짓말이 된다(아직 안 끝난 건은 어느 기간에도 속하지 않는다).
//   집계 원천 — 서버가 period 로 좁혀 준 응답(periodCompleted)이 있으면 그것을,
//   없으면 dailyCompleted 를 그 구간으로 잘라 센다. 어느 쪽이든 화면 숫자는 같은 정의다.
//
//   보충 조회 (2026-08-31) — 부모(CancelAgency.js)는 파라미터 없이 한 번만 부른다.
//   그 응답이 담는 구간은 서버 계약상 **하한만 있는 구간**이다 (utils/cancelDashboard.js
//   computeDailyCompleted: range 없으면 completedDate >= today−29, **상한 없음**).
//   따라서 선택 기간의 시작이 today−29 이후면 부모 응답이 이미 그 기간을 통째로 담고 있고
//   (미래 완료일 포함), 자른 값이 정답이라 추가 왕복을 하지 않는다. 시작이 그보다 앞일 때만
//   이 컴포넌트가 스스로 GET /cancel-agency/dashboard?from&to 를 1회 부른다(기간 키당 1회).
//   ⚠ 부모 응답에는 periodCompleted(수수료)가 없다 — 범위 안 기간에서 수수료가 안 보이는 건
//   결함이 아니라 계약이다. 그래서 요약 줄이 "건수만"이라고 스스로 말한다.
//   같은 기간을 '‹ ›' 로 오가도 재요청하지 않도록 응답은 **기간 키별로** 남긴다(실패도 null 로 기록).
//   실패하면 조용히 부모 데이터를 유지한다 — 대시보드는 토스트로 시끄러워질 화면이 아니다.
//   쓰는 것은 그 응답의 dailyCompleted/period/periodCompleted 뿐. 작업 큐·미수·세금계산서는
//   시점 지표라 언제나 부모 응답(data)에서만 읽는다.
//
// 시각 문법 = 완료 장부(CancelAgencyDoneList.js) / 시안 redesign/done-list.html:
//   ① 상단 요약 스트립 = 큰 숫자 + 단위 + 세로 구분선 (stat-card 4~5장을 대체)
//   ② 카드 = radius 10 / border #e2e8f0 / 타이틀 13px·800·#1B3A5C, 장식 아이콘 없음
//   ③ 수치는 tabular-nums — 자리수가 흔들리면 장부로 안 읽힌다

var C = {
  primary: '#1B3A5C',
  accent: '#2563EB',
  bd: '#e2e8f0',
  bdSoft: '#f1f5f9',
  head: '#f8fafc',
  text: '#0f172a',
  sub: '#5b6b7f',
  muted: '#64748b',
  faint: '#a8b4c4',
  danger: '#b91c1c',
  money: '#0f5132'
};

var NUM = { fontVariantNumeric: 'tabular-nums' };

// 완료 추이 기간 모드 — 'recent' 가 기본이라 기존 화면(최근 30일)이 그대로 유지된다.
var PERIOD_MODES = [['recent', '최근'], ['day', '일'], ['week', '주'], ['month', '월']];
var periodBtn = function (on) {
  return {
    background: on ? '#1B3A5C' : '#fff', color: on ? '#fff' : '#5b6b7f',
    border: '1px solid ' + (on ? '#1B3A5C' : '#dbe3ec'), padding: '2px 9px',
    fontSize: 11.5, fontWeight: 700, cursor: 'pointer', lineHeight: 1.5
  };
};
var navBtn = { background: 'none', border: '1px solid #e2e8f0', borderRadius: 6, width: 26, height: 26, cursor: 'pointer', color: '#334155', fontSize: 12, lineHeight: 1 };

export function fmtNum(n) { return (parseInt(n, 10) || 0).toLocaleString(); }

// ── 요약 스트립 지표 (순수) ────────────────────────────────────────────────
// 앞 4개 = "오늘 처리할 일" 축(작업 큐 4종과 1:1), 뒤 2개 = 경영 축.
// 직원(agency_staff) 계정은 서버가 미수/세금계산서를 아예 안 내려주므로 여기서도 뺀다.
export function dashMetrics(data, isStaff) {
  var k = (data && data.kpi) || {};
  var m = [
    { key: 'new', label: '신규 접수', value: k.newCount || 0, unit: '건' },
    { key: 'overdue', label: '지연', value: k.overdueCount || 0, unit: '건', tone: 'danger' },
    { key: 'hold', label: '보류', value: k.holdCount || 0, unit: '건', tone: 'danger' },
    { key: 'reissue', label: '재교부', value: k.reissueCount || 0, unit: '건' }
  ];
  if (!isStaff) {
    m.push({ key: 'receivable', label: '미수 합계', value: k.monthReceivable || 0, unit: '원', tone: 'money', money: true });
    m.push({ key: 'tax', label: '세금계산서 미발행', value: k.taxInvoiceUnissued || 0, unit: '건', tone: 'danger' });
  }
  return m;
}

// 지표 숫자 색 — 0 은 옅게(할 일 없음), 위험 축은 값이 있을 때만 적색.
export function metricColor(m) {
  if (!m.value) return C.faint;
  if (m.tone === 'money') return C.money;
  if (m.tone === 'danger') return C.danger;
  return C.primary;
}

// ── 화면 ──────────────────────────────────────────────────────────────────

function CancelAgencyDashboard(props) {
  var data = props.data;
  var isMobile = !!props.isMobile;
  var isStaff = !!props.isStaff;
  // 훅은 early return 위에 — 호출 순서가 흔들리면 React 가 죽는다.
  var [mode, setMode] = useState('recent');
  var [anchor, setAnchor] = useState(ymdLocal(new Date()));
  // 기간 키('from~to') → 보충 조회 응답. 값 null = 조회 실패(같은 키 재시도 안 함).
  // 1슬롯이면 '‹ ›' 왕복마다 직전 기간이 밀려나 매번 재요청이 된다 → 키별로 남긴다.
  var [extras, setExtras] = useState({});

  // 선택 기간이 부모 응답의 하한(today−29) 앞으로 나가면 보충 조회가 필요하다.
  // 상한은 없다 — 서버가 range 없이 부를 때 completedDate 상한을 걸지 않으므로
  // 미래 완료일도 부모 응답에 들어 있다 (utils/cancelDashboard.js computeDailyCompleted).
  var today = ymdLocal(new Date());
  var pr = mode === 'recent' ? null : rangeOf(mode, anchor);
  var outside = !!pr && pr[0] < shiftYmd(today, -29);
  var needKey = outside ? (pr[0] + '~' + pr[1]) : '';
  var hasExtra = !!needKey && Object.prototype.hasOwnProperty.call(extras, needKey);

  useEffect(function () {
    if (!needKey || !data) return;                       // '최근' 또는 부모 범위 안 → 추가 요청 없음
    if (Object.prototype.hasOwnProperty.call(extras, needKey)) return;   // 이미 받았거나 실패로 기록된 키
    var alive = true;
    var span = needKey.split('~');
    var keep = function (d) {
      setExtras(function (prev) {
        var next = Object.assign({}, prev);
        next[needKey] = d;
        return next;
      });
    };
    authFetch(API + '/cancel-agency/dashboard?from=' + span[0] + '&to=' + span[1])
      .then(function (r) { return r.json(); })
      // 에러 응답(401/403/400)은 dailyCompleted 가 없다 — 그때는 부모 데이터를 그대로 둔다
      .then(function (d) { if (alive) keep((d && Array.isArray(d.dailyCompleted)) ? d : null); })
      .catch(function () { if (alive) keep(null); });
    return function () { alive = false; };
  }, [needKey, extras, data]);

  if (!data) {
    return (<div className="card" style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>불러오는 중…</div>);
  }

  var q = data.queues || {};
  var rcv = data.receivablesTop || [];
  var tx = data.taxInvoice || {};
  var metrics = dashMetrics(data, isStaff);

  // 완료 추이 기간 — 'recent' = 기존 동작(최근 30일). 나머지는 기준일(anchor) 기준 일/주/월.
  // 원천은 **완료 추이 한정** — 보충 조회분이 그 기간의 것이면 그것을, 아니면 부모 응답을 쓴다.
  // 조회 중 = "이 기간이 필요한데 아직 응답(실패 기록 포함)이 없다". 파생값이라 모드를 바꿔도
  // 로딩이 남지 않는다 (구 setExtraLoading 은 alive 안에서만 내려 전환 시 영구 dim 이었다).
  var extraLoading = !!needKey && !hasExtra;
  var src = (needKey && extras[needKey]) ? extras[needKey] : data;
  var allDaily = src.dailyCompleted || [];
  var daily = pr ? allDaily.filter(function (d) { return d.date >= pr[0] && d.date <= pr[1]; }) : allDaily;
  var maxDaily = daily.reduce(function (m, d) { return Math.max(m, d.count); }, 1);
  // 서버가 같은 구간으로 좁혀 준 요약이 있으면 그것을 쓴다(수수료 포함). 없으면 차트 합으로 건수만.
  var srvPeriod = (pr && src.period && src.period.from === pr[0] && src.period.to === pr[1]) ? src.periodCompleted : null;
  var periodCount = srvPeriod ? srvPeriod.count : daily.reduce(function (s2, d) { return s2 + d.count; }, 0);

  // 카드 공통 — 시안 톤(라운드 10 / 실선 1px / 헤더 옅은 회색)
  var card = { border: '1px solid ' + C.bd, borderRadius: 10, background: '#fff', overflow: 'hidden' };
  var cardHead = {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
    padding: '9px 13px', background: C.head, borderBottom: '1px solid ' + C.bd
  };
  var cardTitle = { fontSize: 13, fontWeight: 800, color: C.primary, letterSpacing: '-.2px' };

  // 작업 큐 행 — 클릭 시 대행요청 목록의 그 행으로 점프(하이라이트). D+3 부터 적색.
  var qRow = function (r) {
    var age = r.ageDays;
    var red = age != null && age >= 3;
    return (
      <div key={r.id} onClick={function () { props.onOpenRequest(r.id); }} role="button"
        style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10,
          padding: isMobile ? '11px 13px' : '7px 13px', minHeight: isMobile ? 44 : 34,
          borderBottom: '1px solid ' + C.bdSoft, cursor: 'pointer', fontSize: 12.5
        }}>
        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            <b style={Object.assign({ fontSize: 13, fontWeight: 800, color: C.text, letterSpacing: '-.2px' }, NUM)}>{r.carNo || '-'}</b>
            <span style={{ marginLeft: 7, color: C.sub }}>{r.requesterCompanyName || r.owner || ''}</span>
            {r.lien_status === 'has_lien' && <span style={{ marginLeft: 6, padding: '1px 6px', borderRadius: 8, fontSize: 10, fontWeight: 700, background: '#fef2f2', color: C.danger }}>압류/저당</span>}
          </span>
          {r.hold_reason ? <span style={{ fontSize: 11, color: '#991b1b', marginTop: 2 }}>사유: {r.hold_reason}</span> : null}
        </div>
        {age != null
          ? <span style={Object.assign({ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, whiteSpace: 'nowrap', background: red ? '#fef2f2' : C.bdSoft, color: red ? C.danger : '#475569' }, NUM)}>D+{age}</span>
          : <span style={Object.assign({ fontSize: 11, color: C.faint, whiteSpace: 'nowrap' }, NUM)}>{r.requestDate || ''}</span>}
      </div>);
  };

  var queueBox = function (title, rows, emptyMsg) {
    return (
      <div style={Object.assign({}, card, { marginBottom: 10 })}>
        <div style={cardHead}>
          <span style={cardTitle}>{title}</span>
          <span style={Object.assign({ fontSize: 12, fontWeight: 800, color: rows.length ? C.sub : C.faint }, NUM)}>{rows.length}</span>
        </div>
        {rows.length > 0 ? rows.map(qRow) : <div style={{ padding: '14px 13px', fontSize: 12, color: C.faint, textAlign: 'center' }}>{emptyMsg}</div>}
      </div>);
  };

  return (<div>
    {/* ① 요약 스트립 — 구 stat-card 격자(신규/지연/보류/미수/세금계산서)를 승격 치환. 재교부가 한 자리 늘었다.
        모바일(≤768)은 한 줄 스트립이 3·2·1 로 어긋나 세로 구분선이 허공에 남는다 → 2열 격자 + 라벨/수치 2단.
        (운영자 6지표 = 3행, 직원 4지표 = 2행 — 어느 쪽도 빈 칸이 남지 않는다) */}
    <div style={{
      display: isMobile ? 'grid' : 'flex', alignItems: isMobile ? 'stretch' : 'center',
      gridTemplateColumns: isMobile ? 'minmax(0,1fr) minmax(0,1fr)' : undefined,   // minmax(0,…) — auto 최소폭이면 긴 금액이 칸을 밀어 페이지가 가로로 넘친다
      columnGap: isMobile ? 12 : undefined, flexWrap: isMobile ? undefined : 'wrap', rowGap: isMobile ? 12 : 10,
      background: 'linear-gradient(180deg,#fbfdff,#f4f8fd)', border: '1px solid #dbe6f3',
      borderRadius: 10, padding: isMobile ? '13px 14px' : '12px 18px', marginBottom: 14
    }}>
      {metrics.map(function (m, i) {
        var last = i === metrics.length - 1;
        if (isMobile) {
          return (<div key={m.key} style={{ minWidth: 0 }}>
            <i style={{ display: 'block', fontStyle: 'normal', fontSize: 11.5, fontWeight: 700, color: C.muted,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.label}</i>
            <span style={{ display: 'flex', alignItems: 'baseline', gap: 5, marginTop: 1, minWidth: 0 }}>
              <b style={Object.assign({ fontSize: m.money ? 19 : 21, fontWeight: 800, letterSpacing: '-.5px', color: metricColor(m) }, NUM)}>{m.money ? fmtNum(m.value) : m.value}</b>
              <em style={{ fontStyle: 'normal', fontSize: 12, fontWeight: 700, color: '#475569' }}>{m.unit}</em>
            </span>
          </div>);
        }
        return (<div key={m.key} style={{
          display: 'flex', alignItems: 'baseline', gap: 7, whiteSpace: 'nowrap',
          paddingRight: last ? 0 : 24, marginRight: last ? 0 : 24,
          borderRight: last ? 'none' : '1px solid #e2eaf4'
        }}>
          <i style={{ fontStyle: 'normal', fontSize: 11.5, fontWeight: 700, color: C.muted }}>{m.label}</i>
          <b style={Object.assign({ fontSize: m.money ? 17 : 20, fontWeight: 800, letterSpacing: '-.5px', color: metricColor(m) }, NUM)}>{m.money ? fmtNum(m.value) : m.value}</b>
          <em style={{ fontStyle: 'normal', fontSize: 12, fontWeight: 700, color: '#475569' }}>{m.unit}</em>
        </div>);
      })}
    </div>

    {/* 폐차 대행 — 차량 무관 건. 줄 전체가 폐차 탭 링크 (기존 동작 유지) */}
    {data.scrap && <div onClick={function () { props.onGoScrap(); }} role="button"
      style={{
        display: 'flex', flexWrap: isMobile ? 'wrap' : undefined, gap: isMobile ? 4 : undefined,
        justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', marginBottom: 14,
        border: '1px solid ' + C.bd, borderRadius: 10, background: '#fff', cursor: 'pointer', fontSize: 12.5
      }}>
      <span style={cardTitle}>폐차 대행</span>
      <span style={Object.assign({ color: C.muted }, NUM)}>
        진행중 <b style={{ color: C.text, fontWeight: 800 }}>{data.scrap.active || 0}</b>
        <span style={{ color: '#c3d0df', margin: '0 7px' }}>·</span>
        이번달 완료 <b style={{ color: C.money, fontWeight: 800 }}>{data.scrap.completedThisMonth || 0}</b>
        <span style={{ color: C.faint, marginLeft: 8 }}>→</span>
      </span>
    </div>}

    {/* ② 작업 큐 / ③ 정산 요약 — 직원은 작업 큐 전체폭 */}
    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'minmax(0,1fr)' : (isStaff ? '1fr' : '1.4fr 1fr'), gap: 16 }}>
      <div>
        <h3 style={{ margin: '0 0 8px', fontSize: 13, fontWeight: 800, color: C.primary, letterSpacing: '-.2px' }}>오늘 할 일</h3>
        {queueBox('신규 접수 대기', q.new || [], '신규 요청 없음')}
        {queueBox('지연 — 접수 후 1일+ 진행 안 됨', q.overdue || [], '지연 건 없음')}
        {queueBox('진행 불가 / 보류 (압류·저당)', q.hold || [], '보류 건 없음')}
        {queueBox('등록증 재교부 필요', q.reissue || [], '재교부 건 없음')}
      </div>
      {!isStaff && <div>
        <h3 style={{ margin: '0 0 8px', fontSize: 13, fontWeight: 800, color: C.primary, letterSpacing: '-.2px' }}>정산 요약</h3>
        <div style={Object.assign({}, card, { marginBottom: 10 })}>
          <div style={cardHead}>
            <span style={cardTitle}>거래처별 미수 Top 5</span>
            <span style={Object.assign({ fontSize: 12.5, fontWeight: 800, color: (data.receivableTotal || 0) > 0 ? C.danger : C.faint }, NUM)}>{fmtNum(data.receivableTotal)}원</span>
          </div>
          {rcv.length > 0 ? rcv.map(function (c) {
            // 행 클릭 = 정산 탭의 그 거래처 장부. 스코프는 구 인코딩(c:회사 / w:비가입 거래처)으로 넘기고
            // 정산 화면이 계정/단위 색인으로 변환한다 (관리자 대시보드 진입과 동일 규약).
            var sc = (c.companyId || 0) > 0 ? ('c:' + c.companyId) : ((c.clientId || 0) > 0 ? ('w:' + c.clientId) : '');
            return (
              <div key={c.key || (c.companyId + '-' + c.clientId)} onClick={function () { props.onGoSettlement(sc); }} role="button"
                style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10,
                  padding: isMobile ? '11px 13px' : '7px 13px', minHeight: isMobile ? 44 : 34,
                  borderBottom: '1px solid ' + C.bdSoft, fontSize: 12.5, cursor: 'pointer'
                }}>
                <span style={{ color: C.sub, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.companyName}</span>
                <span style={Object.assign({ fontWeight: 800, whiteSpace: 'nowrap', color: c.outstanding > 0 ? C.danger : C.money }, NUM)}>{fmtNum(c.outstanding)}</span>
              </div>);
          }) : <div style={{ padding: '14px 13px', fontSize: 12, color: C.faint, textAlign: 'center' }}>미수 없음</div>}
        </div>

        <div style={Object.assign({}, card, { marginBottom: 10 })}>
          <div style={cardHead}><span style={cardTitle}>세금계산서 (이번 달)</span></div>
          <div style={{ padding: '10px 13px', fontSize: 12.5 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', color: C.muted }}>
              <span>발행 완료</span><span style={Object.assign({ fontWeight: 800, color: (tx.issued || 0) > 0 ? C.money : C.faint }, NUM)}>{tx.issued || 0}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', color: C.muted, marginTop: 6 }}>
              <span>미발행</span><span style={Object.assign({ fontWeight: 800, color: (tx.unissued || 0) > 0 ? C.danger : C.faint }, NUM)}>{tx.unissued || 0}</span>
            </div>
            {/* 진입 요청으로 보내 정산 화면이 서브뷰(입금 관리/보관함)를 정리하고 미수 보드에 착지하게 한다 */}
            <button className="btn btn-outline btn-sm" style={{ marginTop: 10, width: '100%', justifyContent: 'center', minHeight: isMobile ? 40 : undefined }}
              onClick={function () { props.onGoSettlement(''); }}>정산 탭에서 발행 →</button>
          </div>
        </div>

        <div style={card}>
          <div style={cardHead}>
            <span style={cardTitle}>완료 추이</span>
            <span style={{ display: 'inline-flex', borderRadius: 7, overflow: 'hidden' }}>
              {PERIOD_MODES.map(function (m) {
                return (<button key={m[0]} type="button" style={periodBtn(mode === m[0])}
                  onClick={function () { setMode(m[0]); }}>{m[1]}</button>);
              })}
            </span>
          </div>
          {/* 기준일 이동 — '최근'(30일 고정)에는 이동할 축이 없다 */}
          {mode !== 'recent' && <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 13px 0' }}>
            <button title="이전" style={navBtn} onClick={function () { setAnchor(stepAnchor(mode, anchor, -1)); }}>‹</button>
            <span style={Object.assign({ flex: 1, textAlign: 'center', fontSize: 12, fontWeight: 700, color: C.sub }, NUM)}>
              {rangeLabel(mode, pr[0], pr[1])}
              {!!extraLoading && <span style={{ marginLeft: 6, fontWeight: 400, color: C.faint }}>조회 중…</span>}
            </span>
            <button title="다음" style={navBtn} onClick={function () { setAnchor(stepAnchor(mode, anchor, 1)); }}>›</button>
          </div>}
          <div style={Object.assign({ padding: '12px 13px' }, extraLoading ? { opacity: 0.55 } : null)}>
            {daily.length > 0
              ? <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 50 }}>
                {daily.map(function (d) {
                  return (<div key={d.date} title={d.date + ': ' + d.count + '건'}
                    style={{ flex: 1, background: d.count ? C.accent : '#e6edf7', borderRadius: '2px 2px 0 0', height: Math.max(3, Math.round(d.count / maxDaily * 46)) + 'px' }} />);
                })}
              </div>
              : <div style={{ height: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: C.faint }}>완료 건 없음</div>}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: 8, fontSize: 12, color: C.muted }}>
              <span>{mode === 'recent' ? '최근 30일' : rangeLabel(mode, pr[0], pr[1])}</span>
              <span style={NUM}>완료 <b style={{ color: C.text, fontWeight: 800 }}>{periodCount}</b>건
                {srvPeriod
                  ? <span> · 수수료 <b style={{ color: C.money, fontWeight: 800 }}>{fmtNum(srvPeriod.fee)}</b>원</span>
                  /* 부모 응답에는 periodCompleted 가 없다 — 보충 조회를 하지 않는 범위 안 기간은 건수만이 정상 */
                  : (!!pr && !outside && <span style={{ color: C.faint }}> · 최근 30일 안은 건수만</span>)}</span>
            </div>
          </div>
        </div>
      </div>}
    </div>
  </div>);
}

export default CancelAgencyDashboard;
